import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import LeaderboardScoreMix from "./LeaderboardScoreMix";
import {
  computePeriodScore,
  dominantDomain,
  formatScore,
  leaderDisplayName,
  levelTone,
  memberInitials,
} from "./leaderboardHelpers";

function RankRow({ row, expanded, onToggle }) {
  const score = computePeriodScore(row);
  const lead = dominantDomain(row);
  const tone = levelTone(row.userLevel);
  const maxScore = row._heatMax || score || 1;
  const heat = Math.min(100, (score / maxScore) * 100);

  return (
    <article className={`alb-rank-row${expanded ? " alb-rank-row-open" : ""}`}>
      <button type="button" className="alb-rank-row-btn" onClick={onToggle}>
        <span className="alb-rank-pos tabular-nums">#{row.position}</span>
        <span className="alb-rank-heat" aria-hidden style={{ width: `${heat}%` }} />
        <span className="alb-rank-avatar">{memberInitials(row.name, row.inviteCode)}</span>
        <span className="alb-rank-body">
          <strong className="alb-rank-name">{leaderDisplayName(row)}</strong>
          <span className="alb-rank-meta">
            <em className={`alb-rank-level alb-rank-level-${tone}`}>
              {row.userLevel || "Rookie"}
            </em>
            {row.inviteCode ? <code>{row.inviteCode}</code> : null}
          </span>
        </span>
        <span className="alb-rank-side">
          <strong className="tabular-nums">{formatScore(score)}</strong>
          <span>pts</span>
          {lead ? (
            <em className="alb-rank-lead" style={{ color: lead.color }}>
              {lead.short}
            </em>
          ) : null}
          <ChevronDown size={14} className={`alb-rank-chevron${expanded ? " alb-rank-chevron-open" : ""}`} />
        </span>
      </button>
      {expanded ? (
        <div className="alb-rank-sheet">
          <LeaderboardScoreMix row={row} />
          {row.allTimeScores != null ? (
            <p className="alb-rank-alltime">
              All-time score · <strong className="tabular-nums">{formatScore(row.allTimeScores)}</strong>
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default function LeaderboardRankStream({
  rows = [],
  loading,
  emptyLabel = "No ranked members on this page.",
}) {
  const [expandedId, setExpandedId] = useState(null);
  const maxScore = rows.reduce((m, r) => Math.max(m, computePeriodScore(r)), 0) || 1;
  const enriched = rows.map((row) => ({ ...row, _heatMax: maxScore }));

  if (loading && !rows.length) {
    return (
      <div className="alb-stream alb-stream-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="alb-rank-row-skeleton" aria-hidden />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return <p className="alb-stream-empty">{emptyLabel}</p>;
  }

  return (
    <div className="alb-stream">
      {enriched.map((row) => (
        <RankRow
          key={row._id}
          row={row}
          expanded={expandedId === row._id}
          onToggle={() =>
            setExpandedId((prev) => (prev === row._id ? null : row._id))
          }
        />
      ))}
    </div>
  );
}
